import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SuiteCard, SuiteButton, SuiteBadge } from "@/components/suite";
import { formatDateIT } from "@/foundation/formatting";
import { useEffectiveRole } from "@/foundation/context";
import {
  Inbox, Check, X, Search, ClipboardList, Clock, CheckCircle2, XCircle, Ban,
  Calendar, User, FileText, RefreshCw,
} from "lucide-react";

const STATUS_META = {
  pending:   { label: "In attesa",  tone: "warn",    icon: Clock },
  approved:  { label: "Approvata",  tone: "ok",      icon: CheckCircle2 },
  rejected:  { label: "Respinta",   tone: "err",     icon: XCircle },
  cancelled: { label: "Annullata",  tone: "neutral", icon: Ban },
};

const KIND_LABELS = {
  FERIE: "Ferie",
  PERMESSI: "Permessi / ROL",
  EX_FESTIVITA: "Ex festività",
  PERMESSI_STUDIO: "Permessi studio",
};

function fmtDate(iso) {
  return formatDateIT(iso, "—");
}

function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 1;
  const a = new Date(fromIso), b = new Date(toIso);
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

export default function LeaveRequestsInbox() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const { companies: globalCompanies, activeCompanyId, isGlobalCompanyRole } = useActiveCompany();
  const canFilterCompany = ["admin", "super_admin", "studio_operator", "external_consultant"].includes(effectiveRole);

  const [status, setStatus] = useState("pending");
  const [companyId, setCompanyId] = useState(activeCompanyId || "");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Decision dialog
  const [decision, setDecision] = useState(null); // { req, mode: "approve"|"reject" }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (canFilterCompany) setCompanyId(activeCompanyId || "");
  }, [activeCompanyId, canFilterCompany]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (canFilterCompany && companyId) params.company_id = companyId;
      const { data } = await api.get("/leave-requests", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [status, companyId, canFilterCompany]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((r) =>
      (r._employee_name || "").toLowerCase().includes(term) ||
      (KIND_LABELS[r.kind] || r.kind || "").toLowerCase().includes(term) ||
      (r.notes || "").toLowerCase().includes(term)
    );
  }, [items, q]);

  const counters = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    for (const r of items) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [items]);

  const openDecision = (req, mode) => { setDecision({ req, mode }); setReason(""); };
  const closeDecision = () => { setDecision(null); setReason(""); };

  const submitDecision = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      const { req, mode } = decision;
      if (mode === "approve") {
        await api.post(`/leave-requests/${req.id}/approve`);
        toast.success("Richiesta approvata · le presenze sono state generate");
      } else {
        await api.post(`/leave-requests/${req.id}/reject`, { reason: reason.trim() });
        toast.success("Richiesta respinta · il dipendente è stato notificato");
      }
      closeDecision();
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const isPending = status === "pending";

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="leave-inbox-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-suite-primary">
            <ClipboardList size={12} />
            <span>Operatività</span>
          </div>
          <h1 className="mt-1 text-[26px] font-bold tracking-tight text-suite-neutral-900" style={{ fontFamily: "Manrope" }}>
            Richieste ferie e permessi
          </h1>
          <p className="text-[13px] text-suite-neutral-500 mt-0.5 max-w-2xl">
            Approva o respingi le richieste dei dipendenti. Le approvazioni generano automaticamente le presenze e notificano il dipendente.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <SuiteButton size="sm" variant="secondary" icon={RefreshCw} onClick={load} data-testid="inbox-refresh">
            Aggiorna
          </SuiteButton>
        </div>
      </div>

      {/* Counters chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="inbox-counters">
        {[
          { key: "pending",   label: "In attesa",  count: counters.pending,   tone: "border-amber-200 bg-amber-50 text-amber-800", icon: Clock },
          { key: "approved",  label: "Approvate",  count: counters.approved,  tone: "border-emerald-200 bg-emerald-50 text-emerald-800", icon: CheckCircle2 },
          { key: "rejected",  label: "Respinte",   count: counters.rejected,  tone: "border-red-200 bg-red-50 text-red-700", icon: XCircle },
          { key: "cancelled", label: "Annullate",  count: counters.cancelled, tone: "border-slate-200 bg-slate-50 text-slate-700", icon: Ban },
        ].map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setStatus(c.key)}
            className={`text-left rounded-lg border px-3 py-2.5 transition-all hover:shadow-sm ${c.tone} ${
              status === c.key ? "ring-2 ring-suite-primary/40" : ""
            }`}
            data-testid={`chip-${c.key}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">{c.label}</div>
              <c.icon size={14} className="opacity-60" />
            </div>
            <div className="mt-1 font-mono text-[22px] font-bold leading-none">{c.count}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <SuiteCard padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Ricerca libera</Label>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-2.5 text-suite-neutral-400" />
              <Input
                className="pl-7 h-9"
                placeholder="Nome dipendente, causale, nota…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="inbox-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Stato</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-44" data-testid="inbox-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="approved">Approvate</SelectItem>
                <SelectItem value="rejected">Respinte</SelectItem>
                <SelectItem value="cancelled">Annullate</SelectItem>
                <SelectItem value="all">Tutti</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {canFilterCompany && isGlobalCompanyRole && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-suite-neutral-500 block mb-1">Azienda</Label>
              <Select value={companyId || "__all__"} onValueChange={(v) => setCompanyId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-64" data-testid="inbox-company"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte le aziende</SelectItem>
                  {globalCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </SuiteCard>

      {/* List */}
      <SuiteCard padding="sm">
        {loading ? (
          <div className="py-8 text-center text-[12px] text-suite-neutral-500">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center" data-testid="inbox-empty">
            <Inbox className="mx-auto h-8 w-8 text-suite-neutral-300" />
            <div className="mt-2 text-[13px] text-suite-neutral-500">
              {isPending ? "Nessuna richiesta in attesa. Ottimo lavoro." : "Nessuna richiesta trovata per i filtri correnti."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="inbox-table">
              <thead>
                <tr className="border-b border-suite-neutral-200 text-left text-[10px] uppercase tracking-widest text-suite-neutral-500">
                  <th className="py-2 pr-2">Dipendente</th>
                  <th className="py-2 pr-2">Causale</th>
                  <th className="py-2 pr-2">Periodo</th>
                  <th className="py-2 pr-2 text-right">Giorni</th>
                  <th className="py-2 pr-2 text-right">Ore/gg</th>
                  <th className="py-2 pr-2">Note</th>
                  <th className="py-2 pr-2">Stato</th>
                  <th className="py-2 pl-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const st = STATUS_META[r.status] || STATUS_META.pending;
                  const days = daysBetween(r.start_date, r.end_date);
                  return (
                    <tr key={r.id} className="border-b border-suite-neutral-100 hover:bg-suite-neutral-50/60" data-testid={`inbox-row-${r.id}`}>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <User size={13} className="text-suite-neutral-400 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-suite-neutral-900 truncate">{r._employee_name || "—"}</div>
                            {r.matricola && <div className="text-[10.5px] text-suite-neutral-500 font-mono">Matr. {r.matricola}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1 rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-1.5 py-0.5 text-[11px] font-medium text-suite-neutral-700">
                          {KIND_LABELS[r.kind] || r.kind}
                        </span>
                      </td>
                      <td className="py-2 pr-2 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-suite-neutral-700">
                          <Calendar size={12} className="text-suite-neutral-400" />
                          <span className="font-mono">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-right font-mono">{days}</td>
                      <td className="py-2 pr-2 text-right font-mono">{r.hours_per_day ?? "—"}</td>
                      <td className="py-2 pr-2 max-w-[240px]">
                        <div className="truncate text-suite-neutral-600" title={r.notes || ""}>{r.notes || "—"}</div>
                        {r.status === "rejected" && r.rejection_reason && (
                          <div className="mt-0.5 text-[10.5px] text-red-700 italic">Motivo: {r.rejection_reason}</div>
                        )}
                      </td>
                      <td className="py-2 pr-2">
                        <SuiteBadge tone={st.tone}>
                          <st.icon size={11} strokeWidth={2} />
                          {st.label}
                        </SuiteBadge>
                      </td>
                      <td className="py-2 pl-2 text-right whitespace-nowrap">
                        {r.status === "pending" ? (
                          <>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => openDecision(r, "approve")}
                              className="h-8 px-2 mr-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              data-testid={`approve-${r.id}`}
                            >
                              <Check size={13} className="mr-1" />
                              <span className="text-xs">Approva</span>
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => openDecision(r, "reject")}
                              className="h-8 px-2 border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`reject-${r.id}`}
                            >
                              <X size={13} className="mr-1" />
                              <span className="text-xs">Respingi</span>
                            </Button>
                          </>
                        ) : (
                          <span className="text-[11px] text-suite-neutral-400">
                            {r.decided_at ? `Decisa il ${fmtDate(r.decided_at)}` : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SuiteCard>

      {/* Dialog decisione */}
      <Dialog open={!!decision} onOpenChange={(v) => !v && closeDecision()}>
        <DialogContent formDialog className="max-w-lg" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision?.mode === "approve" ? (
                <><CheckCircle2 className="text-emerald-600" size={20} /> Conferma approvazione</>
              ) : (
                <><XCircle className="text-red-600" size={20} /> Conferma rifiuto</>
              )}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-3 py-1">
              <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 p-3 text-[12.5px] space-y-1">
                <div><span className="font-semibold">Dipendente:</span> {decision.req._employee_name || "—"}</div>
                <div><span className="font-semibold">Causale:</span> {KIND_LABELS[decision.req.kind] || decision.req.kind}</div>
                <div>
                  <span className="font-semibold">Periodo:</span> {fmtDate(decision.req.start_date)} → {fmtDate(decision.req.end_date)}
                  <span className="ml-2 text-suite-neutral-500">({daysBetween(decision.req.start_date, decision.req.end_date)} giorni)</span>
                </div>
                {decision.req.notes && <div><span className="font-semibold">Nota dipendente:</span> {decision.req.notes}</div>}
              </div>
              {decision.mode === "approve" ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 flex gap-2">
                  <FileText size={14} className="shrink-0 mt-0.5" />
                  <div>
                    Confermando <b>approvi la richiesta</b>: il sistema creerà automaticamente le presenze
                    nei giorni lavorativi del periodo con la causale scelta e invierà notifica al dipendente.
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs mb-1 block">Motivo del rifiuto (facoltativo)</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="Es. Copertura organico insufficiente in quel periodo"
                    data-testid="decision-reason"
                  />
                  <div className="text-[10.5px] text-suite-neutral-500 mt-1">
                    Il motivo sarà mostrato al dipendente insieme alla notifica di rifiuto.
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDecision} disabled={busy} data-testid="decision-cancel">Annulla</Button>
            <SuiteButton
              onClick={submitDecision}
              loading={busy}
              variant={decision?.mode === "approve" ? "primary" : "danger"}
              icon={decision?.mode === "approve" ? Check : X}
              data-testid="decision-confirm"
            >
              {decision?.mode === "approve" ? "Approva richiesta" : "Respingi richiesta"}
            </SuiteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
